import React, { createContext, useCallback, useContext, useState } from 'react';
import { FieldValues } from 'react-hook-form';

import { TSubmittedData } from '@/shared/components/ui/form/form.types';
import {
  TFormWizardContext,
  TProjectProviderProps,
} from '@/shared/components/ui/form-wizard/context/form-wizard-context.types';

const FormWizardContext = createContext<TFormWizardContext<any> | undefined>(
  undefined,
);

export const useFormWizard = <
  TFormValues extends FieldValues = FieldValues,
>() => {
  const context = useContext(FormWizardContext) as
    | TFormWizardContext<TFormValues>
    | undefined;
  if (!context) {
    throw new Error('useFormWizard must be used within FormWizardProvider');
  }
  return context;
};

export const FormWizardProvider = <TFormValues extends FieldValues>({
  children,
}: TProjectProviderProps) => {
  const [stepsData, setStepsData] = useState<TSubmittedData<TFormValues>[]>([]);
  const [lastFormIndex, setLastFormIndex] = useState<number>(0);
  const [step, setStep] = useState<number>(0);
  const [dirtySteps, setDirtySteps] = useState<boolean[]>([]);
  // Sticky: revisiting a step re-seeds its defaults from what was entered, so it
  // reads pristine again — a later `false` must not erase an edit already made.
  const markStepDirty = useCallback((index: number, dirty: boolean) => {
    setDirtySteps((previous) =>
      previous[index] || !dirty
        ? previous
        : Object.assign([...previous], { [index]: true }),
    );
  }, []);
  const resetForm = useCallback(() => {
    setStepsData([]);
    setStep(0);
    setLastFormIndex(0);
    setDirtySteps([]);
  }, []);

  return (
    <FormWizardContext
      value={{
        stepsData,
        setStepsData,
        lastFormIndex,
        setLastFormIndex,
        step,
        setStep,
        dirtySteps,
        markStepDirty,
        resetForm,
      }}
    >
      {children}
    </FormWizardContext>
  );
};
